import { orderBy } from 'lodash';
import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateToken, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { Undoable } from '@medsurf/flat-decorators';
import * as FlatStates from '../../internal';
import { HistoryEntityBaseState } from '../base/history-entity.base.state';

export const SLIDE_LAYER_ENTITY_TOKEN = new StateToken<SlideLayerEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.LAYER_ENTITY);

export interface SlideLayerEntityStateModel extends FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.LayerEntityModels.Layer> {
  layerIndex: number;
}

@State<SlideLayerEntityStateModel>({
  name: SLIDE_LAYER_ENTITY_TOKEN,
  defaults: {
    layerIndex: 0,
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class SlideLayerEntityState extends HistoryEntityBaseState<FlatModels.LayerEntityModels.Layer, SlideLayerEntityStateModel> {
  //<editor-fold desc="Selectors">

  @Selector([SlideLayerEntityState])
  static layerIndex$(state: SlideLayerEntityStateModel): number {
    return state.layerIndex;
  }
	
  @Selector([SlideLayerEntityState])
  static override typedEntities$(state: SlideLayerEntityStateModel): { [id: string]: FlatModels.LayerEntityModels.Layer } {
    return HistoryEntityBaseState.typedEntities$(state);
  }
	
  @Selector([SlideLayerEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.LayerEntityModels.Layer }): FlatModels.LayerEntityModels.Layer[] {
    return HistoryEntityBaseState.entities$(typedEntities);
  }
	
  @Selector([SlideLayerEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.LayerEntityModels.Layer }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }
	
  @Selector([SlideLayerEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.LayerEntityModels.Layer }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }
	
  @Selector([
    SlideLayerEntityState.typedEntities$,
    FlatStates.PageEntityState.currentSelectedSlidePage$
  ])
  static currentSelectedTypedSlideLayers$(typedEntities: { [id: string]: FlatModels.LayerEntityModels.Layer },
                                          currentSelectedSlidePage: FlatModels.PageEntityModels.Slide | null) {
    if (currentSelectedSlidePage && currentSelectedSlidePage.layers) {
      return currentSelectedSlidePage.layers.reduce((arr, id) => {
        const entity = typedEntities[id];
        if (entity) {
          arr[entity.id] = entity;
        }
        return arr;
      }, {} as { [id: string]: FlatModels.LayerEntityModels.Layer });
    }
    return {};
  }
	
  @Selector([SlideLayerEntityState.currentSelectedTypedSlideLayers$])
  static currentSelectedSlideLayers$(currentSelectedTypedSlideLayers: { [id: string]: FlatModels.LayerEntityModels.Layer }) {
    return Object.values(currentSelectedTypedSlideLayers);
  }
  
  @Selector([SlideLayerEntityState.currentSelectedSlideLayers$])
  static slideLayersArray$(currentSelectedSlideLayers: FlatModels.LayerEntityModels.Layer[]) {
    return currentSelectedSlideLayers.reduce((arr, layer) => {
      arr.push({
        value: 'layer-' + layer.position,
        name: layer.window || FlatModels.LayerEntityModels.Window.null.toString()
      });
      return arr;
    }, [] as {value: string, name: string | null}[]);
  }

  @Selector([SlideLayerEntityState.currentSelectedSlideLayers$])
  static currentSelectedSlideLayersMap$(currentSelectedSlideLayers: FlatModels.LayerEntityModels.Layer[]) {
    return orderBy(currentSelectedSlideLayers, (a) => a.position || 0, 'asc').map(l =>  l.id);
  }

  @Selector([
    SlideLayerEntityState.currentSelectedTypedSlideLayers$,
    SlideLayerEntityState.currentSelectedSlideLayersMap$,
    SlideLayerEntityState.layerIndex$
  ])
  static currentSelectedSlideLayer$(currentSelectedTypedSlideLayers: { [id: string]: FlatModels.LayerEntityModels.Layer },
                                    currentSelectedSlideLayersMap: string[],
                                    layerIndex: number) {
    return currentSelectedTypedSlideLayers[currentSelectedSlideLayersMap[layerIndex]] || null;
  }
  
  static layersWithImage$(imageId: string) {
    return createSelector([SlideLayerEntityState.entities$], (layers: FlatModels.LayerEntityModels.Layer[]) => {
      return layers.reduce((arr, layer) => {
        if (layer.images.includes(imageId)) {
          arr.push(layer);
        }
        return arr;
      }, [] as FlatModels.LayerEntityModels.Layer[]);
    });
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.SlideLayerEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<SlideLayerEntityStateModel>,
                            action: FlatActions.SlideLayerEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }

  @Action(FlatActions.SlideLayerEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<SlideLayerEntityStateModel>,
                              action: FlatActions.SlideLayerEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }

  @Action(FlatActions.SlideLayerEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<SlideLayerEntityStateModel>,
                               action: FlatActions.SlideLayerEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }

  @Action(FlatActions.SlideLayerEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<SlideLayerEntityStateModel>,
                                 action: FlatActions.SlideLayerEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  @Action(FlatActions.SlideLayerEntityActions.AddEntity)
  @Undoable(
    FlatActions.SlideLayerEntityActions.AddEntity,
    SLIDE_LAYER_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<SlideLayerEntityStateModel>,
                     action: FlatActions.SlideLayerEntityActions.AddEntity) {
    return super.addEntity(state, action);
  }

  @Action(FlatActions.SlideLayerEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.SlideLayerEntityActions.UpdateEntity,
    SLIDE_LAYER_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<SlideLayerEntityStateModel>,
                        action: FlatActions.SlideLayerEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }
	
  @Action(FlatActions.SlideLayerEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.SlideLayerEntityActions.RemoveEntity,
    SLIDE_LAYER_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<SlideLayerEntityStateModel>,
                        action: FlatActions.SlideLayerEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">
	
  @Action(FlatActions.SlideLayerEntityActions.UndoSnapshot)
  override undoSnapshot(state: StateContext<SlideLayerEntityStateModel>,
                        action: FlatActions.SlideLayerEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }
	
  @Action(FlatActions.SlideLayerEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<SlideLayerEntityStateModel>,
                        action: FlatActions.SlideLayerEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }
	
  @Action(FlatActions.SlideLayerEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<SlideLayerEntityStateModel>,
                            action: FlatActions.SlideLayerEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }

  //</editor-fold>
	
  @Action(FlatActions.SlideLayerEntityActions.SetLayerIndex)
  setLayerIndex({patchState}: StateContext<SlideLayerEntityStateModel>,
                {layerIndex}: FlatActions.SlideLayerEntityActions.SetLayerIndex) {
    patchState({
      layerIndex
    });
  }
	
  @Action(FlatActions.SlideLayerEntityActions.RearrangeLayers)
  rearrangeLayers({getState, dispatch}: StateContext<SlideLayerEntityStateModel>,
                  {layers}: FlatActions.SlideLayerEntityActions.RearrangeLayers) {
    // Get Entities
    const entities = structuredClone(getState().entities);

    // Iterate Through Layers
    for (let i = 0; i < layers.length; i++) {
      // Get Layer
      const layer = layers[i];

      // Get Entity
      let entity;
      if (typeof layer === 'string') {
        entity = entities[layer];
      } else {
        entity = entities[layer.id];
      }

      // Update Entity
      entity.position = i;

      // Dispatch Update
      dispatch(new FlatActions.SlideLayerEntityActions.UpdateEntity(entity));
    }
  }

  //</editor-fold>
}
